import { Combobox } from '@headlessui/react'
import { ChevronDown } from '@/assets/icons'
import { MenuItemContent } from '@/components/menu/MenuItemContent'
import { getMenuItemClassName } from '@/components/menu/menu-item-styles'
import clsx from 'clsx'
import { useMemo, useRef, useState } from 'react'
import { JurisdictionKind, getJurisdictionPickerValue, type JurisdictionFieldState } from '../jurisdiction-field-state'
import { FEDERAL_DISTRICT_LABELS, STATE_LABELS } from '../jurisdiction-maps'

type JurisdictionFieldProps = JurisdictionFieldState & {
  onChange: (state: JurisdictionFieldState) => void
  error?: string
  showUndecided?: boolean
  idPrefix?: string
}

const jurisdictionKindOptions: Array<{ value: JurisdictionKind; label: string }> = [
  { value: JurisdictionKind.STATE, label: 'State' },
  { value: JurisdictionKind.FEDERAL, label: 'Federal' },
  { value: JurisdictionKind.UNDECIDED, label: 'Undecided' },
]

export type JurisdictionMatchMode = 'prefix' | 'contains'

export function normalizeSearchText(value: string): string {
  return value.toLowerCase().replace(/\./g, '').replace(/\s+/g, ' ').trim()
}

export function filterJurisdictionOptions(options: Record<string, string>, query: string, matchMode: JurisdictionMatchMode = 'contains'): string[] {
  const trimmed = normalizeSearchText(query)
  if (!trimmed) return Object.keys(options)

  return Object.keys(options).filter((option) => {
    const searchableParts = [options[option], option].map(normalizeSearchText)
    if (matchMode === 'prefix') {
      return searchableParts.some((part) => part.startsWith(trimmed) || part.split(/[\s-]+/).some((word) => word.startsWith(trimmed)))
    }

    const searchableText = searchableParts.join(' ')
    return searchableText.includes(trimmed)
  })
}

export function JurisdictionCombobox(props: {
  id: string
  options: Record<string, string>
  value: string | null
  onChange: (value: string | null) => void
  placeholder: string
  error?: string
  matchMode?: JurisdictionMatchMode
}) {
  const { id, options, value, onChange, placeholder, error, matchMode = 'contains' } = props
  const [query, setQuery] = useState('')
  const buttonRef = useRef<HTMLButtonElement>(null)

  const selectedOption = value

  const filteredOptions = useMemo(() => filterJurisdictionOptions(options, query, matchMode), [options, query, matchMode])

  return (
    <Combobox
      value={selectedOption}
      onChange={(option: string | null) => {
        onChange(option)
        setQuery('')
      }}
    >
      {({ open }) => (
        <div>
          <div className="relative">
            <Combobox.Input
              id={id}
              className={clsx(
                'min-h-[56px] w-full rounded border bg-white py-4 pl-3 pr-10 text-base tracking-[-0.32px] text-brand-neutral-950 placeholder:text-brand-neutral-600 focus:outline-none focus:ring-0',
                error ? 'border-red-500 focus:border-red-500' : 'border-brand-neutral-400 focus:border-brand-400'
              )}
              displayValue={(option: string | null) => (option ? options[option] ?? option : '')}
              onChange={(event) => setQuery(event.target.value)}
              onClick={() => {
                // Headless UI doesn't open the panel on input click; trigger the button unless it's already open.
                if (!open) buttonRef.current?.click()
              }}
              placeholder={placeholder}
            />
            <Combobox.Button ref={buttonRef} className="absolute inset-y-0 right-0 flex cursor-text items-center pr-3">
              <ChevronDown className="h-6 w-6 text-brand-neutral-600" aria-hidden="true" />
            </Combobox.Button>
            <Combobox.Options className="absolute z-50 mt-1 flex max-h-80 w-full flex-col gap-0.5 overflow-auto rounded-md border border-brand-neutral-200 bg-white p-2 text-sm shadow-lg">
              {filteredOptions.length === 0 ? (
                <div className="px-3 py-2 text-brand-neutral-500">No matches</div>
              ) : (
                filteredOptions.map((option) => (
                  <Combobox.Option
                    key={option}
                    value={option}
                    className={({ active, selected }) => getMenuItemClassName({ highlighted: active, selected, variant: 'neutral' })}
                  >
                    {({ active, selected }) => <MenuItemContent highlighted={active} label={options[option]} selected={selected} variant="neutral" />}
                  </Combobox.Option>
                ))
              )}
            </Combobox.Options>
          </div>
          {error && <p className="mt-1 text-xs text-red-700">{error}</p>}
        </div>
      )}
    </Combobox>
  )
}

export default function JurisdictionField(props: JurisdictionFieldProps) {
  const { kind, value, onChange, error, showUndecided = true, idPrefix = 'jurisdiction' } = props

  const pickerOptions = kind === JurisdictionKind.FEDERAL ? FEDERAL_DISTRICT_LABELS : STATE_LABELS
  const pickerPlaceholder = kind === JurisdictionKind.FEDERAL ? 'Search U.S. District Courts *' : 'Search U.S. States & Territories *'
  const toggleOptions = showUndecided ? jurisdictionKindOptions : jurisdictionKindOptions.filter((option) => option.value !== JurisdictionKind.UNDECIDED)

  const handleKindChange = (nextKind: JurisdictionKind) => onChange({ kind: nextKind, value: value })

  return (
    <div className="space-y-3">
      <div className="space-y-3">
        <p className="text-base font-medium leading-6 tracking-[-0.32px] text-brand-neutral-950">Jurisdiction</p>
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2" role="radiogroup" aria-label="Jurisdiction type">
          {toggleOptions.map((option) => (
            <label key={option.value} className="flex cursor-pointer items-center gap-2 text-base leading-6 tracking-[-0.32px] text-brand-neutral-950">
              <input
                type="radio"
                name={`${idPrefix}-kind`}
                value={option.value}
                checked={kind === option.value}
                className="peer sr-only"
                onChange={() => handleKindChange(option.value)}
              />
              <span
                aria-hidden="true"
                className={clsx(
                  'size-5 shrink-0 rounded-full bg-white peer-focus-visible:outline peer-focus-visible:outline-2 peer-focus-visible:outline-offset-2 peer-focus-visible:outline-brand-500',
                  kind === option.value ? 'border-[5px] border-brand-500' : 'border border-brand-neutral-300'
                )}
              />
              {option.label}
            </label>
          ))}
        </div>
      </div>

      {kind !== JurisdictionKind.UNDECIDED && (
        <JurisdictionCombobox
          id={`${idPrefix}-picker`}
          options={pickerOptions}
          value={getJurisdictionPickerValue(kind, value)}
          onChange={(nextValue) => onChange({ kind, value: nextValue })}
          placeholder={pickerPlaceholder}
          error={error}
          matchMode={kind === JurisdictionKind.STATE ? 'prefix' : 'contains'}
        />
      )}
    </div>
  )
}
